/**
 * TUVI - Transcripción para el Cotizador Web.
 * Módulo de página entera. La lista principal muestra las actividades ya
 * transcritas y se filtra en TIEMPO REAL del lado del cliente. El botón
 * "+ Añadir" abre un modal que busca en TODA la matriz para transcribir nuevas.
 * Standalone — no vinculado a cotizaciones.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './TranscripcionCotizador.css';

const norm = (s) => (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim().replace(/\s+/g, ' ');

const TranscripcionCotizador = ({ onVolver }) => {
  const { success, error } = useNotification();
  const [transcritas, setTranscritas] = useState([]);
  const [totalActividades, setTotalActividades] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [edits, setEdits] = useState({});
  const [guardando, setGuardando] = useState(null);

  // ---- Modal "Añadir" ----
  const [modalOpen, setModalOpen] = useState(false);
  const [mBusqueda, setMBusqueda] = useState('');
  const [mRows, setMRows] = useState([]);
  const [mLoading, setMLoading] = useState(false);
  const [mEdits, setMEdits] = useState({});
  const [mGuardando, setMGuardando] = useState(null);

  const cargarTranscritas = useCallback(async () => {
    setLoading(true);
    try {
      const d = await client.query(
        `query { transcripcionCotizador(solo_transcritas: true) { rows { codigo actividad categoria capitulo en_matriz traduccion } total_actividades } }`
      );
      const r = d.transcripcionCotizador;
      setTranscritas(r.rows || []);
      setTotalActividades(r.total_actividades || 0);
    } catch (e) { error('Error al cargar las transcripciones'); }
    finally { setLoading(false); }
  }, [error]);

  useEffect(() => { cargarTranscritas(); }, [cargarTranscritas]);

  // Filtro en tiempo real (cliente) de las ya transcritas
  const filtradas = useMemo(() => {
    if (!busqueda.trim()) return transcritas;
    const palabras = norm(busqueda).split(' ').filter(Boolean);
    return transcritas.filter(r => {
      const t = norm([r.codigo, r.actividad, r.traduccion].filter(Boolean).join(' '));
      return palabras.every(w => t.includes(w));
    });
  }, [transcritas, busqueda]);

  // Mutación compartida
  const guardarTraduccion = async (codigo, traduccion) => {
    const r = await client.mutate(
      `mutation($c: String!, $t: String!) { guardarTranscripcionCotizador(codigo: $c, traduccion: $t) { success message } }`,
      { c: codigo, t: traduccion }
    );
    return r.guardarTranscripcionCotizador;
  };

  // ---- Edición inline en la lista principal ----
  const valorEdit = (row) => (edits[row.codigo] !== undefined ? edits[row.codigo] : (row.traduccion || ''));
  const esDirty = (row) => edits[row.codigo] !== undefined && edits[row.codigo] !== (row.traduccion || '');

  const guardarMain = async (row) => {
    const trad = (valorEdit(row) || '').trim();
    setGuardando(row.codigo);
    try {
      const res = await guardarTraduccion(row.codigo, trad);
      if (res.success) {
        if (!trad) {
          setTranscritas(prev => prev.filter(x => x.codigo !== row.codigo));
          success('Transcripción eliminada');
        } else {
          setTranscritas(prev => prev.map(x => x.codigo === row.codigo ? { ...x, traduccion: trad } : x));
          success('Guardado');
        }
        setEdits(prev => { const n = { ...prev }; delete n[row.codigo]; return n; });
      } else error(res.message);
    } catch (e) { error('Error al guardar'); }
    finally { setGuardando(null); }
  };

  const eliminarFila = async (row) => {
    if (!window.confirm(`¿Eliminar la transcripción de ${row.codigo}?`)) return;
    setGuardando(row.codigo);
    try {
      const r = await client.mutate(
        `mutation($c: String!) { eliminarTranscripcionCotizador(codigo: $c) { success message } }`,
        { c: row.codigo }
      );
      if (r.eliminarTranscripcionCotizador.success) {
        setTranscritas(prev => prev.filter(x => x.codigo !== row.codigo));
        setEdits(prev => { const n = { ...prev }; delete n[row.codigo]; return n; });
        setMRows(prev => prev.map(x => x.codigo === row.codigo ? { ...x, traduccion: null } : x));
        success('Transcripción eliminada');
      } else error(r.eliminarTranscripcionCotizador.message);
    } catch (e) { error('Error al eliminar'); }
    finally { setGuardando(null); }
  };

  // ---- Modal: búsqueda en toda la matriz (backend, debounced) ----
  useEffect(() => {
    if (!modalOpen) return;
    const t = setTimeout(async () => {
      if (!mBusqueda.trim()) { setMRows([]); return; }
      setMLoading(true);
      try {
        const d = await client.query(
          `query($b: String) { transcripcionCotizador(busqueda: $b, solo_transcritas: false) { rows { codigo actividad categoria capitulo en_matriz traduccion } } }`,
          { b: mBusqueda }
        );
        setMRows(d.transcripcionCotizador.rows || []);
      } catch (e) { error('Error al buscar en la matriz'); }
      finally { setMLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [mBusqueda, modalOpen, error]);

  const mValor = (row) => (mEdits[row.codigo] !== undefined ? mEdits[row.codigo] : (row.traduccion || ''));

  const guardarModal = async (row) => {
    const trad = (mValor(row) || '').trim();
    if (!trad) { error('Escribe la nueva descripción'); return; }
    setMGuardando(row.codigo);
    try {
      const res = await guardarTraduccion(row.codigo, trad);
      if (res.success) {
        success(row.traduccion ? 'Transcripción actualizada' : 'Transcripción añadida');
        setMRows(prev => prev.map(x => x.codigo === row.codigo ? { ...x, traduccion: trad } : x));
        setMEdits(prev => { const n = { ...prev }; delete n[row.codigo]; return n; });
        cargarTranscritas(); // refresca la lista principal
      } else error(res.message);
    } catch (e) { error('Error al guardar'); }
    finally { setMGuardando(null); }
  };

  const cerrarModal = () => { setModalOpen(false); setMBusqueda(''); setMRows([]); setMEdits({}); };

  return (
    <div className="trc-container">
      <div className="trc-header">
        <div>
          <button className="trc-volver" onClick={onVolver}>← Volver a la matriz</button>
          <h1>📝 Transcripción - Cotizador Web</h1>
          <p>Asigna a cada actividad una descripción nueva (comercial) para el cotizador web.</p>
        </div>
        <div className="trc-contador">
          <span className="trc-contador-num">{transcritas.length}</span>
          <span className="trc-contador-lbl">actividades transcritas</span>
        </div>
      </div>

      <div className="trc-toolbar">
        <input
          className="trc-search"
          type="text"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          placeholder="Filtrar entre las transcritas (código o descripción)..."
        />
        {busqueda.trim() && <button className="trc-clear" onClick={() => setBusqueda('')}>Limpiar</button>}
        <button className="trc-btn-add" onClick={() => setModalOpen(true)}>+ Añadir transcripción</button>
      </div>

      <div className="trc-info">
        Mostrando <strong>{filtradas.length}</strong> de <strong>{transcritas.length}</strong> transcritas. Para transcribir una nueva actividad de la matriz ({totalActividades} en total), usa <strong>+ Añadir transcripción</strong>.
      </div>

      {loading ? (
        <div className="trc-empty">Cargando...</div>
      ) : filtradas.length === 0 ? (
        <div className="trc-empty">{busqueda.trim() ? 'Ninguna transcrita coincide con la búsqueda.' : 'Aún no hay transcripciones. Usa "+ Añadir transcripción".'}</div>
      ) : (
        <div className="trc-table-wrap">
          <table className="trc-table">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Código</th>
                <th style={{ width: '38%' }}>Descripción original (matriz)</th>
                <th>Nueva descripción (cotizador web)</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map(row => (
                <tr key={row.codigo} className="trc-row-ok">
                  <td className="trc-codigo">
                    {row.codigo}
                    {!row.en_matriz && <span className="trc-tag-huerf" title="Este código ya no está en la matriz">sin matriz</span>}
                  </td>
                  <td className="trc-orig">
                    {row.actividad || <span className="trc-muted">— (no está en la matriz)</span>}
                    {row.actividad && (
                      <button className="trc-copiar" title="Copiar la original como base" onClick={() => setEdits(prev => ({ ...prev, [row.codigo]: row.actividad }))}>⎘ usar como base</button>
                    )}
                  </td>
                  <td>
                    <textarea
                      className="trc-textarea"
                      rows={2}
                      value={valorEdit(row)}
                      onChange={e => setEdits(prev => ({ ...prev, [row.codigo]: e.target.value }))}
                      placeholder="Descripción para el cotizador... (vacío = eliminar)"
                    />
                  </td>
                  <td className="trc-acc">
                    {esDirty(row)
                      ? <button className="trc-guardar" disabled={guardando === row.codigo} onClick={() => guardarMain(row)}>{guardando === row.codigo ? '...' : 'Guardar'}</button>
                      : <span className="trc-ok">✓</span>}
                    <button className="trc-eliminar" title="Eliminar transcripción" disabled={guardando === row.codigo} onClick={() => eliminarFila(row)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== Modal: añadir transcripción buscando en toda la matriz ===== */}
      {modalOpen && (
        <div className="trc-modal-overlay" onClick={cerrarModal}>
          <div className="trc-modal" onClick={e => e.stopPropagation()}>
            <div className="trc-modal-header">
              <h3>Añadir transcripción — buscar en la matriz</h3>
              <button className="trc-modal-close" onClick={cerrarModal}>×</button>
            </div>
            <div className="trc-modal-search">
              <input
                autoFocus
                type="text"
                value={mBusqueda}
                onChange={e => setMBusqueda(e.target.value)}
                placeholder="Busca en toda la matriz por código o descripción..."
              />
            </div>
            <div className="trc-modal-body">
              {mLoading ? (
                <div className="trc-modal-hint">Buscando...</div>
              ) : !mBusqueda.trim() ? (
                <div className="trc-modal-hint">Escribe para buscar actividades de la matriz y transcribirlas.</div>
              ) : mRows.length === 0 ? (
                <div className="trc-modal-hint">No se encontraron actividades.</div>
              ) : (
                mRows.map(row => (
                  <div key={row.codigo} className={`trc-mrow ${row.traduccion ? 'ya' : ''}`}>
                    <div className="trc-mrow-top">
                      <span className="trc-codigo">{row.codigo}</span>
                      {row.traduccion && <span className="trc-mrow-badge">ya transcrita</span>}
                    </div>
                    <div className="trc-mrow-orig">{row.actividad || <span className="trc-muted">— (no está en la matriz)</span>}</div>
                    <div className="trc-mrow-edit">
                      <textarea
                        rows={2}
                        value={mValor(row)}
                        onChange={e => setMEdits(prev => ({ ...prev, [row.codigo]: e.target.value }))}
                        placeholder="Nueva descripción para el cotizador..."
                      />
                      <button className="trc-guardar" disabled={mGuardando === row.codigo} onClick={() => guardarModal(row)}>
                        {mGuardando === row.codigo ? '...' : (row.traduccion ? 'Actualizar' : 'Añadir')}
                      </button>
                      {row.traduccion && (
                        <button className="trc-eliminar" title="Eliminar transcripción" disabled={guardando === row.codigo} onClick={() => eliminarFila(row)}>🗑️</button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="trc-modal-footer">
              <button className="trc-clear" onClick={cerrarModal}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscripcionCotizador;
